import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  InputBase,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useLocation, useParams } from 'react-router-dom';
import useCallbackDebounce from '../../../../../hooks/useCallbackDebounce';
import useToggle from '../../../../../hooks/useToggle';
import UserContext from '../../../../../shared-components/contexts/UserContext';
import NoItemBox from '../../../../../shared-components/NoItemBox';
import TabPanel from '../../../../../shared-components/TabPanel';
import { IPagedRequest } from '../../../../../shared-interfaces/IPagedRequest';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../../../../shared-interfaces/User/ICoreUser';
import CookieService from '../../../../../shared-services/CookieService';
import UserService, {
  SearchTypes,
} from '../../../../../shared-services/User/UserService';
import { Role, RoleToString } from '../../../../../shared-types/Roles';
import AddNewConnectionDialog from './Components/AddNewConnectionDialog';
import ConnectionListItem, {
  ConnectionListItemSkeletonLoader,
} from './Components/ConnectionListItem';
import ConnectionsService, {
  IGetAdvisors,
} from './Services/ConnectionsService';

const useStyles = makeStyles({
  avatar: {
    width: '80px',
    height: '80px',
    marginRight: '16px',
  },
  listItem: {
    borderBottom: '1px solid silver',
  },
  listHeading: {
    fontWeight: 600,
  },
  subtitle2: {
    fontStyle: 'italic',
    color: '#666',
  },
  content: {
    backgroundColor: 'white',
    padding: '30px',
    boxShadow: '2px 2px 0 rgba(0,0,0,.1)',
    borderLeft: `1px solid ${grey[300]}`,
    borderTop: `1px solid ${grey[300]}`,
  },
  search: {
    border: '1px solid gray',
    borderRadius: '2px',
    paddingLeft: '8px',
    width: '200px',
    maxWidth: '200px',
  },
});

enum ConnectionsTabs {
  loadingTab = 0,
  advisors = 1,
  advisees = 2,
}

const Connections = () => {
  const classes = useStyles();
  const location = useLocation();
  const _user = useContext(UserContext);

  const [openAddAdvisorDialog, toggleOpenAddAdvisorDialog] = useToggle();

  const [userError, setUserError] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(ConnectionsTabs.loadingTab);

  const [user, setUser] = useState<ICoreUser>({} as ICoreUser);
  const [advisors, setAdvisors] = useState<ICoreUser[]>([]);
  const [advisees, setAdvisees] = useState<ICoreUser[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const [adviseeEOL, setAdviseeEOL] = useState(false);
  const [advisorEOL, setAdvisorEOL] = useState(false);

  //@ts-ignore
  const { externalId } = useParams();

  useEffect(() => {
    //@ts-ignore
    if (location.state && location.state.user) {
      //@ts-ignore
      setUser(location.state?.user as ICoreUser);
    } else {
      UserService.search({
        term: externalId,
        inNetwork: false,
        enrolledOnly: false,
        SearchType: SearchTypes.matchOnExternalId,
        skip: 0,
        take: 1,
      }).then(u => {
        if (u.totalCount > 0) setUser(u.items[0]);
        else setUserError(true);
      });
    }
  }, []);

  useEffect(() => {
    if (user.id) {
      loadAdvisors(1).then(() =>
        loadAdvisees(1)
          .then(() => {
            setInitialLoading(false);
          })
          .finally(() => {
            if (user.role <= Role.PeerAdvisor) {
              setActiveTab(ConnectionsTabs.advisors);
            } else {
              setActiveTab(ConnectionsTabs.advisees);
            }
          }),
      );
    }
  }, [user]);

  async function loadAdvisees(page: number) {
    if (user.role >= Role.PeerAdvisor) {
      if (!initialLoading) {
        setIsLoading(true);
      }

      const u = await ConnectionsService.getAdvisees({
        page: page,
        pageSize: 25,
        searchParameters: {
          term: searchTerm.length > 0 ? searchTerm : undefined,
          userId: user.id,
        } as IGetAdvisors,
      } as IPagedRequest<IGetAdvisors>);
      setAdvisees([...advisees, ...u.items]);
      setAdviseeEOL(page >= u.totalPages);
    }
  }

  async function loadAdvisors(page: number) {
    if (user.role <= Role.PeerAdvisor) {
      if (!initialLoading) {
        setIsLoading(true);
      }

      const u = await ConnectionsService.getAdvisors({
        page: page,
        pageSize: 25,
        searchParameters: {
          term: searchTerm.length > 0 ? searchTerm : undefined,
          userId: user.id,
        },
      } as IPagedRequest<IGetAdvisors>);
      setAdvisors([...advisors, ...u.items]);
      setAdvisorEOL(page >= u.totalPages);
    }
  }

  const handleChange = (
    event: React.ChangeEvent<{}>,
    newValue: ConnectionsTabs,
  ) => {
    setActiveTab(newValue);
  };

  const handleSearch = (term: string) => {
    setIsSearching(true);
    setAdvisors([]);
    setAdvisees([]);
    setSearchTerm(term);
  };

  useCallbackDebounce(
    () => {
      if (!initialLoading) {
        loadAdvisors(1).then(() => {
          loadAdvisees(1).then(() => {
            setIsSearching(false);
            setIsLoading(false);
          });
        });
      }
    },
    500,
    [searchTerm],
  );

  const HeadingSkeleton = () => {
    return (
      <ListItem ContainerComponent="div">
        <ListItemAvatar>
          <Skeleton variant="circular" className={classes.avatar} />
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={<Skeleton variant="text" height={28} width={250} />}
          secondary={
            <>
              <div>
                <Skeleton variant="text" width={150} />
              </div>
              <div>
                <Skeleton variant="text" width={100} />
              </div>
            </>
          }
        />
      </ListItem>
    );
  };

  return (
    <>
      <Box display="flex">
        <Box flexGrow={1}>
          <Typography variant="h1">Manage Connections</Typography>
        </Box>
        <Box>
          {_user.isAdministrator && user.role <= Role.PeerAdvisor && (
            <Button
              onClick={() => toggleOpenAddAdvisorDialog()}
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
            >
              <Hidden smDown>Add Advisor Connection</Hidden>
              <Hidden smUp>New</Hidden>
            </Button>
          )}
        </Box>
      </Box>
      <Paper className={classes.content}>
        {userError ? (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            User Not Found
          </Alert>
        ) : (
          <>
            <Grid container spacing={4}>
              <Grid item sm={12}>
                {Object.keys(user).length === 0 ? (
                  <HeadingSkeleton />
                ) : (
                  <ListItem ContainerComponent="div">
                    <ListItemAvatar>
                      <Avatar
                        className={classes.avatar}
                        src={
                          user.profileImageFilePath ||
                          user.institutionProfileImageFilePath
                        }
                        alt={`image for ${GetCoreUserFormattedName(
                          user,
                          false,
                        )}`}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="h2" style={{ marginBottom: '0' }}>
                          {GetCoreUserFormattedName(user, false)}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="subtitle1">
                            {user.externalId}
                          </Typography>
                          <div>{RoleToString(user.role)}</div>
                        </>
                      }
                    />
                    <ListItemSecondaryAction style={{ top: '30px' }}>
                      <form>
                        <InputBase
                          autoComplete="on"
                          placeholder="Search Connections"
                          inputProps={{
                            height: '60px',
                            'aria-label': 'Search Connections',
                          }}
                          className={classes.search}
                          value={searchTerm}
                          onChange={e => handleSearch(e.target.value)}
                          endAdornment={
                            <InputAdornment position="end">
                              {searchTerm.length > 0 && (
                                <IconButton
                                  disableRipple
                                  onClick={() => handleSearch('')}
                                  size="large"
                                >
                                  <ClearIcon />
                                </IconButton>
                              )}
                            </InputAdornment>
                          }
                        />
                      </form>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
              </Grid>
              {CookieService.Get(`BeaconConnectionChanged_${user.id}`) ===
                'true' && (
                <Grid item sm={12}>
                  <Alert severity="warning">
                    A change in connections may take up to 5 minutes to appear.
                    Leaving this page will not interrupt this process.
                  </Alert>
                </Grid>
              )}
              <Grid item sm={12} md={12} lg={12}>
                {user && (
                  <div>
                    <div
                      style={{
                        backgroundColor: 'white',
                        position: 'sticky',
                        top: '85px',
                        zIndex: 998,
                      }}
                    >
                      <Tabs
                        value={activeTab}
                        onChange={handleChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                        aria-label="connections tabs"
                      >
                        <Tab
                          value={ConnectionsTabs.loadingTab}
                          label="Loading..."
                          style={{ display: 'none' }}
                          aria-hidden="true"
                          id={`vertical-tab-${ConnectionsTabs.loadingTab}`}
                        />
                        {!initialLoading && user.role <= Role.PeerAdvisor && (
                          <Tab
                            value={ConnectionsTabs.advisors}
                            label="Advisors to this user"
                            id={`vertical-tab-${ConnectionsTabs.advisors}`}
                          />
                        )}
                        {!initialLoading && user.role >= Role.PeerAdvisor && (
                          <Tab
                            value={ConnectionsTabs.advisees}
                            label="Students this user advises"
                            id={`vertical-tab-${ConnectionsTabs.advisees}`}
                          />
                        )}
                      </Tabs>
                    </div>
                    <TabPanel
                      value={activeTab}
                      index={ConnectionsTabs.loadingTab}
                    >
                      <List>
                        {[...Array(25)].map((x, i) => {
                          return <ConnectionListItemSkeletonLoader key={i} />;
                        })}
                      </List>
                    </TabPanel>

                    <TabPanel
                      value={activeTab}
                      index={ConnectionsTabs.advisors}
                    >
                      {!initialLoading &&
                      !isSearching &&
                      advisors.length > 0 ? (
                        <>
                          <InfiniteScroll
                            element="ul"
                            style={{
                              listStyle: 'none',
                              margin: 0,
                              padding: 0,
                              position: 'relative',
                            }}
                            useWindow={true}
                            initialLoad={false}
                            hasMore={!advisorEOL && !isLoading}
                            threshold={500}
                            loadMore={p => {
                              loadAdvisors(p + 1).then(() => {
                                setIsLoading(false);
                              });
                            }}
                          >
                            {advisors.map(x => {
                              return (
                                <ConnectionListItem
                                  key={x.id}
                                  user={x}
                                  selectedUser={user}
                                  connectionType="advisor"
                                  isNew={
                                    user.advisorList.findIndex(
                                      uid => uid === x.id,
                                    ) === -1
                                  }
                                  onRemove={(s, a) => {
                                    setAdvisors(
                                      advisors.filter(u => u.id !== a.id),
                                    );
                                  }}
                                />
                              );
                            })}
                          </InfiniteScroll>
                          {isLoading && <LinearProgress aria-busy={true} />}
                        </>
                      ) : isSearching ? (
                        <List>
                          {[...Array(25)].map((x, i) => {
                            return <ConnectionListItemSkeletonLoader key={i} />;
                          })}
                        </List>
                      ) : !isSearching && advisors.length === 0 ? (
                        <NoItemBox
                          height={575}
                          text="No Connections To Display"
                        />
                      ) : (
                        <></>
                      )}
                    </TabPanel>

                    <TabPanel
                      value={activeTab}
                      index={ConnectionsTabs.advisees}
                    >
                      {!initialLoading &&
                      !isSearching &&
                      advisees.length > 0 ? (
                        <>
                          <InfiniteScroll
                            element="ul"
                            style={{
                              listStyle: 'none',
                              margin: 0,
                              padding: 0,
                              position: 'relative',
                            }}
                            useWindow={true}
                            initialLoad={false}
                            hasMore={!adviseeEOL && !isLoading}
                            loadMore={p => {
                              loadAdvisees(p + 1).then(() => {
                                setIsLoading(false);
                              });
                            }}
                          >
                            {advisees.map(x => {
                              return (
                                <ConnectionListItem
                                  key={x.id}
                                  user={x}
                                  selectedUser={user}
                                  connectionType="advisee"
                                  onRemove={(s, a) => {
                                    setAdvisees(
                                      advisees.filter(u => u.id !== s.id),
                                    );
                                  }}
                                />
                              );
                            })}
                          </InfiniteScroll>
                          {isLoading && <LinearProgress aria-busy={true} />}
                        </>
                      ) : isSearching ? (
                        <List>
                          {[...Array(25)].map((x, i) => {
                            return <ConnectionListItemSkeletonLoader key={i} />;
                          })}
                        </List>
                      ) : !isSearching && advisees.length === 0 ? (
                        <NoItemBox
                          height={575}
                          text="No Connections To Display"
                        />
                      ) : (
                        <></>
                      )}
                    </TabPanel>
                  </div>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      <AddNewConnectionDialog
        open={openAddAdvisorDialog}
        onClose={toggleOpenAddAdvisorDialog}
        student={user}
        onAddConnections={a => {
          setAdvisors([...a, ...advisors]);
        }}
      />
    </>
  );
};

export default Connections;
